import { inject } from '@angular/core';
import { WorkShiftService } from '@core/services';
import { map } from 'rxjs/operators';
import { plainToInstance } from 'class-transformer';
import { EventEntity, EventsList, Pagination, UserEntity } from '@core/entities';
import { Observable } from 'rxjs';
import { GenerateImageUrlWithToken, GetUserId } from '@core/utils';
import { SectionType } from '@core/enums';

export class UseWorkShift {
  private readonly _shiftService = inject(WorkShiftService);

  getAllEventsWithShifts(pagination: Pagination): Observable<EventsList> {
    return this._shiftService.find(pagination).pipe(
      map((response: any) => {
        if (response) {
          return {
            pagination: response.pagination,
            events: response.events.map((item: any) => {
              const transformedItem = plainToInstance(EventEntity, item);
              transformedItem.eventShifts.forEach((shift: any) => {
                shift.shiftUsers = shift.shiftAssign.map((user: any) => plainToInstance(UserEntity, user.users));
                if (shift.upload) shift.imageUrl = GenerateImageUrlWithToken(shift.id, SectionType.EVENT_SHIFT);
              });
              transformedItem.initialDate = transformedItem.recurringDates[0].dateFrom;
              transformedItem.initialTime = `${transformedItem.recurringDates[0].startTime} - ${transformedItem.recurringDates[0].endTime}`;
              transformedItem.shiftsCount = transformedItem.eventShifts.length || 0;
              return transformedItem;
            }),
          } as EventsList;
        }
        return null;
      }),
    );
  }

  getAllEventsWithoutShifts(): Observable<EventEntity[]> {
    return this._shiftService.getEventsWithoutShifts(+GetUserId()).pipe(
      map((response: any) => {
        if (response) {
          return response.map((item: any) => {
            const transformedEntity = plainToInstance(EventEntity, item);
            transformedEntity.imageUrl = GenerateImageUrlWithToken(transformedEntity.id, SectionType.EVENT);
            return transformedEntity;
          });
        }
        return null;
      }),
    );
  }

  getEventDetailsForShift(eventId: number): Observable<EventEntity> {
    return this._shiftService.getEventsWithoutShifts(+GetUserId(), eventId).pipe(
      map((response: any) => {
        // find the event from response array compairing it to the eventId
        const event = response.find((event: EventEntity) => event.id === eventId);

        const transformedEntity = plainToInstance(EventEntity, event);
        transformedEntity.imageUrl = GenerateImageUrlWithToken(transformedEntity.id, SectionType.EVENT);
        return transformedEntity;
      }),
    );
  }

  getEventWithShiftsByEventId(eventId: number): Observable<EventEntity> {
    return this._shiftService.get(eventId).pipe(
      map((response: any) => {
        const transformedItem = plainToInstance(EventEntity, response);
        transformedItem.eventShifts.forEach((shift: any) => {
          shift.shiftUsers = shift.shiftAssign.map((user: any) => plainToInstance(UserEntity, user.users));
          if (shift.upload) shift.imageUrl = GenerateImageUrlWithToken(shift.id, SectionType.EVENT_SHIFT);
        });

        return transformedItem;
      }),
    );
  }

  createShifts(formData: FormData) {
    return this._shiftService.create(formData);
  }

  updateShifts(formData: FormData, eventId: number) {
    return this._shiftService.update(formData, eventId);
  }

  deleteAllShifts(eventId: number) {
    return this._shiftService.deleteAll(eventId);
  }

  deleteShift(shiftId: number) {
    return this._shiftService.delete(shiftId);
  }

  downloadShiftDocument(shiftID: number, fileName: string) {
    return this._shiftService.downloadDocument({ id: shiftID, name: fileName });
  }
}
